import type { IApiError } from "src/@types/deposit-reconciliation";

interface ToastMessage {
  variant?: "destructive";
  description: string;
  duration: number;
}

export const MESSAGES = {
  EXPORT: {
    SUCCESS: (extension: string): ToastMessage => ({
      description: `Arquivo ${extension.toUpperCase()} gerado com sucesso.`,
      duration: 3000,
    }),
    EMPTY: {
      variant: "destructive",
      description: "Nenhum dado para exportar com os filtros aplicados.",
      duration: 4000,
    } satisfies ToastMessage,
    ERROR: (label: string): ToastMessage => ({
      variant: "destructive",
      description: `Falha ao exportar ${label}.`,
      duration: 5000,
    }),
  },
  DRAFT: {
    SAVED: (draftId: number): ToastMessage => ({
      description: `Rascunho #${draftId} salvo.`,
      duration: 3000,
    }),
    DISCARDED: (draftId: number): ToastMessage => ({
      description: `Rascunho #${draftId} descartado.`,
      duration: 3000,
    }),
    DISCARD_ERROR: (error: IApiError): ToastMessage => ({
      variant: "destructive",
      description: error?.data?.message ?? "Falha ao descartar rascunho.",
      duration: 5000,
    }),
    SAVE_ERROR: (error: IApiError): ToastMessage => ({
      variant: "destructive",
      description: draftSaveErrorDescription(error),
      duration: 5000,
    }),
  },
  COMMIT: {
    SUCCESS: (batchCount: number): ToastMessage => ({
      description:
        batchCount === 1
          ? "1 conciliação concluída."
          : `${batchCount} conciliações concluídas.`,
      duration: 4000,
    }),
    ERROR: (error: IApiError): ToastMessage => ({
      variant: "destructive",
      description: commitErrorDescription(error),
      duration: 6000,
    }),
  },
  BATCH: {
    UNDONE: (batchId: number): ToastMessage => ({
      description: `Conciliação #${batchId} desfeita.`,
      duration: 4000,
    }),
    UNDO_ERROR: (error: IApiError): ToastMessage => ({
      variant: "destructive",
      description: batchUndoErrorDescription(error),
      duration: 5000,
    }),
  },
};

function draftSaveErrorDescription(error: IApiError): string {
  if (error?.status === 409) {
    return "Algum item já está em outro rascunho ou conciliação ativa.";
  }
  if (error?.status === 422) {
    return "A soma das NSUs não bate com a soma dos depósitos.";
  }
  return error?.data?.message ?? "Falha ao salvar rascunho.";
}

function commitErrorDescription(error: IApiError): string {
  if (error?.status === 409) {
    return (
      error?.data?.message ??
      "Algum rascunho ficou em conflito. Nenhuma conciliação criada."
    );
  }
  if (error?.status === 400) {
    return "Nenhum rascunho encontrado para confirmar.";
  }
  return error?.data?.message ?? "Falha ao confirmar conciliações.";
}

function batchUndoErrorDescription(error: IApiError): string {
  if (error?.status === 403) {
    return "Apenas administradores podem desfazer conciliações.";
  }
  if (error?.status === 409) {
    return "Esta conciliação já foi desfeita.";
  }
  if (error?.status === 404) {
    return "Conciliação não encontrada.";
  }
  return error?.data?.message ?? "Falha ao desfazer conciliação.";
}

export const TEXTS = {
  COMMON: {
    LOADING: "Carregando…",
    INTERNAL_SERVER_ERROR: "ERRO INTERNO NO SERVIDOR",
  },
  TABLE: {
    CONCILIATED_AT: "Conciliado em",
    CONCILIATED_BY: "Conciliado por",
  },
  RECONCILIATION: {
    INVALID_PERIOD: "Informe um período válido (início e fim) para ver a conciliação.",
    BACK_TO_CURRENT_WEEK: "Voltar para a semana atual",
    CLEAR_SELECTION: "Limpar seleção",
    SHOW_RECONCILED: "Ver itens já conciliados",
    SAVE_DRAFT: "Salvar Rascunho",
    SAVING_DRAFT: "Salvando…",
    COMMIT: "Conciliar",
    DRAFT_DETAILS_HINT: "Clique no rascunho para ver detalhes",
    PENDING_DRAFTS: (count: number): string =>
      `${count} rascunho${count !== 1 ? "s" : ""} pendente${
        count !== 1 ? "s" : ""
      }`,
  },
  DRAFT: {
    TITLE: (draftId: number): string => `Rascunho #${draftId}`,
    SUMMARY: (
      nsuCount: number,
      depositCount: number,
      totalAmount: string,
    ): string =>
      `${nsuCount} NSU${nsuCount !== 1 ? "s" : ""} · ${depositCount} dep · ${totalAmount}`,
    DISCARD_TITLE: (draftId: number): string =>
      `Descartar Rascunho #${draftId}?`,
    DISCARD_DESCRIPTION: (nsuCount: number, depositCount: number): string =>
      `Os ${nsuCount} NSU(s) e ${depositCount} depósito(s) voltarão a ficar disponíveis para nova seleção.`,
    LOADING: "Carregando rascunhos…",
    EMPTY: "Nenhum rascunho neste recorte.",
    NSU_SECTION: "NSUs",
    DEPOSIT_SECTION: "Depósitos",
    CANCEL: "Cancelar",
    DISCARD: "Descartar",
  },
  BATCH: {
    TITLE: (batchId: number): string => `Conciliação #${batchId}`,
    UNDONE_LABEL: "Conciliação desfeita",
    UNDO_ACTION: "Desfazer esta Conciliação",
    ALREADY_UNDONE: "Já desfeito",
    SUMMARY: (storeCode: number, totalAmount: string): string =>
      `Loja ${storeCode} · ${totalAmount}`,
    UNDONE_BY: "Por:",
    UNDONE_AT: "Em:",
    UNDONE_REASON: "Motivo:",
    CREATED_BY: "Criado por:",
    COMMITTED_AT: "Confirmado em:",
    PERIOD: "Período:",
    NSU_SECTION: (count: number): string => `NSUs (${count})`,
    DEPOSIT_SECTION: (count: number): string => `Depósitos (${count})`,
    COLUMN_NUMBER: "Número",
    COLUMN_AMOUNT: "Valor",
    COLUMN_BANK: "Banco",
    COLUMN_DATE: "Data",
  },
};
